import { Scene, StandardMaterial, PBRMaterial, Color3, DynamicTexture, Texture } from "@babylonjs/core";
import { isTouchDevice } from "../util/device.js";

/**
 * Shared materials for the whole mall. With ~40 shop units the per-zone
 * material pattern would create hundreds of duplicate materials; every
 * repeated fixture (walls, floors, glass, frames, wood) draws from this
 * per-scene cache instead.
 *
 * Everything architectural is PBR (metallic/roughness) and lit by the scene's
 * IBL environment + the skylight sun (see lighting.ts). The old StandardMaterial
 * emissive lifts are gone: they were compensating for the missing global
 * illumination and were the main reason the mall read as a washed-out toy.
 * Pure light sources (neon strips) and fake-shadow decals stay Standard.
 */
export interface SharedMaterials {
  /** Emissive cool-white LED for accent tubes/strips (glows under the GlowLayer). */
  neonAccent: StandardMaterial;
  /** Soft radial blob for grounding props/avatars where the shadow map can't. */
  contactShadow: StandardMaterial;
  atriumFloor: PBRMaterial;
  corridorFloor: PBRMaterial;
  shopFloor: PBRMaterial;
  shopWall: PBRMaterial;
  corridorWall: PBRMaterial;
  ceiling: PBRMaterial;
  glass: PBRMaterial;
  frame: PBRMaterial;
  woodFixture: PBRMaterial;
  metalFixture: PBRMaterial;
  planter: PBRMaterial;
  foliage: PBRMaterial;
  foodCourtFloor: PBRMaterial;
  /** Ribbed dark tread for escalator steps. */
  escalatorStep: PBRMaterial;
}

const CACHE: WeakMap<Scene, SharedMaterials> = new WeakMap();

/** Max out anisotropic filtering so tiled floors/walls stay sharp at the
 *  glancing angles a first-person camera sees them from. Babylon's default
 *  level (4) leaves high-repeat textures mip-blurred in the distance. */
function sharpen(scene: Scene, tex: Texture): Texture {
  tex.anisotropicFilteringLevel = scene.getEngine().getCaps().maxAnisotropy;
  return tex;
}

/** Touch devices load the 512px ".m.jpg" texture variants (10-40KB each vs
 *  0.2-2MB originals; generated by PIL alongside the originals in
 *  public/textures). Phones neither need nor can afford 2K surface maps,
 *  and the download difference dominates mobile load time. */
function texUrl(base: string): string {
  return isTouchDevice() ? base.replace(/\.jpg$/, ".m.jpg") : base;
}

/** Base PBR material. Legacy (non-physical) light falloff keeps the existing
 *  PointLight intensities across the zones meaningful; physical falloff would
 *  need every lamp in the mall re-tuned. */
function pbr(scene: Scene, name: string, roughness: number, metallic: number): PBRMaterial {
  const mat: PBRMaterial = new PBRMaterial(name, scene);
  mat.roughness = roughness;
  mat.metallic = metallic;
  mat.usePhysicalLightFalloff = false;
  return mat;
}

function flat(
  scene: Scene,
  name: string,
  color: string,
  roughness: number,
  metallic: number = 0
): PBRMaterial {
  const mat: PBRMaterial = pbr(scene, name, roughness, metallic);
  mat.albedoColor = Color3.FromHexString(color);
  return mat;
}

/** Real photo-based tiling material (CC0 textures from ambientCG, bundled in
 *  /public/textures). tint multiplies the albedo map so one texture serves
 *  several palettes. Falls back to a flat color under jest's NullEngine
 *  (no DOM, no image loading). */
function texturedSurface(
  scene: Scene,
  name: string,
  colorUrl: string,
  normalUrl: string,
  uvScale: number,
  tint: string,
  roughness: number,
  metallic: number,
  fallback: () => PBRMaterial
): PBRMaterial {
  if (typeof document === "undefined") {
    return fallback();
  }
  const mat: PBRMaterial = pbr(scene, name, roughness, metallic);
  const color: Texture = new Texture(texUrl(colorUrl), scene);
  color.uScale = uvScale;
  color.vScale = uvScale;
  mat.albedoTexture = sharpen(scene, color);
  const normal: Texture = new Texture(texUrl(normalUrl), scene);
  normal.uScale = uvScale;
  normal.vScale = uvScale;
  mat.bumpTexture = sharpen(scene, normal);
  mat.albedoColor = Color3.FromHexString(tint);
  return mat;
}

/** Dark metal tread with lengthwise grooves, like real escalator steps.
 *  Procedural (DynamicTexture) so it works everywhere, tests included. */
function ribbedTread(scene: Scene): PBRMaterial {
  const mat: PBRMaterial = pbr(scene, "mm-esc-step", 0.4, 0.85);
  if (typeof document === "undefined") {
    mat.albedoColor = new Color3(0.24, 0.25, 0.27);
    return mat;
  }
  const tex: DynamicTexture = new DynamicTexture("mm-esc-step-tex", { width: 256, height: 256 }, scene, true);
  const ctx: CanvasRenderingContext2D = tex.getContext() as CanvasRenderingContext2D;
  ctx.fillStyle = "#3c4046";
  ctx.fillRect(0, 0, 256, 256);
  ctx.fillStyle = "#22252a";
  for (let px: number = 2; px < 256; px += 8) {
    ctx.fillRect(px, 0, 3, 256);
  }
  tex.update();
  sharpen(scene, tex);
  mat.albedoTexture = tex;
  return mat;
}

/** Polished large-format porcelain tile: near-white field, hairline joints,
 *  low roughness so the IBL environment and lit storefronts sheen across it
 *  like a real mall floor. uvScale sets how many tiles repeat across the
 *  mesh's UV span. */
function polishedTiles(
  scene: Scene,
  name: string,
  base: string,
  joint: string,
  uvScale: number
): PBRMaterial {
  const mat: PBRMaterial = pbr(scene, name, 0.22, 0);
  if (typeof document === "undefined") {
    mat.albedoColor = Color3.FromHexString(base);
    return mat;
  }
  const size: number = 512;
  const tex: DynamicTexture = new DynamicTexture(`${name}-tex`, { width: size, height: size }, scene, true);
  const ctx: CanvasRenderingContext2D = tex.getContext() as CanvasRenderingContext2D;
  ctx.fillStyle = base;
  ctx.fillRect(0, 0, size, size);
  // Very subtle tonal variation so the field isn't a flat fill.
  ctx.fillStyle = "rgba(0, 0, 0, 0.02)";
  for (let i: number = 0; i < 40; i++) {
    const x: number = (i * 97) % size;
    const y: number = (i * 211) % size;
    ctx.fillRect(x, y, 90, 90);
  }
  // Hairline joints on the tile edges (texture wraps, so edges tile).
  ctx.strokeStyle = joint;
  ctx.lineWidth = 2;
  ctx.strokeRect(1, 1, size - 2, size - 2);
  tex.update();
  tex.wrapU = Texture.WRAP_ADDRESSMODE;
  tex.wrapV = Texture.WRAP_ADDRESSMODE;
  tex.uScale = uvScale;
  tex.vScale = uvScale;
  sharpen(scene, tex);
  mat.albedoTexture = tex;
  // Screenshot-tuned: a truly near-white albedo saturates past 1.0 under the
  // sun and clips every shadow to invisible white. Real porcelain reflects
  // ~75-85%, which is exactly what keeps the lighting readable.
  mat.albedoColor = new Color3(0.78, 0.78, 0.788);
  return mat;
}

function brightWall(
  scene: Scene,
  name: string,
  uvScale: number,
  tint: string
): PBRMaterial {
  return texturedSurface(
    scene, name, "/textures/plaster_color.jpg", "/textures/plaster_normal.jpg", uvScale, tint, 0.85, 0,
    () => flat(scene, name, tint, 0.85)
  );
}

/** Soft radial gradient decal: black plane fading to fully transparent at the
 *  rim. Placed just above the floor under props/avatars it reads as ambient
 *  contact shadow on every device tier, including where the shadow map is off. */
function contactShadowMaterial(scene: Scene): StandardMaterial {
  const mat: StandardMaterial = new StandardMaterial("mm-contact-shadow", scene);
  mat.diffuseColor = new Color3(0, 0, 0);
  mat.specularColor = new Color3(0, 0, 0);
  mat.emissiveColor = new Color3(0, 0, 0);
  mat.disableLighting = true;
  if (typeof document === "undefined") {
    mat.alpha = 0;
    return mat;
  }
  const size: number = 128;
  const tex: DynamicTexture = new DynamicTexture("mm-contact-shadow-tex", { width: size, height: size }, scene, false);
  const ctx: CanvasRenderingContext2D = tex.getContext() as CanvasRenderingContext2D;
  ctx.clearRect(0, 0, size, size);
  const g: CanvasGradient = ctx.createRadialGradient(size / 2, size / 2, 0, size / 2, size / 2, size / 2);
  // Strong center: verified live that 0.5 reads as nothing against the lit
  // floor once tone mapping is applied; 0.62 lands as a believable AO pool.
  g.addColorStop(0, "rgba(255, 255, 255, 0.62)");
  g.addColorStop(0.6, "rgba(255, 255, 255, 0.3)");
  g.addColorStop(1, "rgba(255, 255, 255, 0)");
  ctx.fillStyle = g;
  ctx.fillRect(0, 0, size, size);
  tex.update();
  tex.hasAlpha = true;
  mat.opacityTexture = tex;
  return mat;
}

export function getSharedMaterials(scene: Scene): SharedMaterials {
  const cached: SharedMaterials | undefined = CACHE.get(scene);
  if (cached !== undefined) {
    return cached;
  }
  const glass: PBRMaterial = pbr(scene, "mm-glass", 0.05, 0);
  glass.albedoColor = new Color3(0.65, 0.75, 0.8);
  glass.alpha = 0.22;

  // Cool-white LED strip, like the balcony/cove lighting in a modern
  // minimalist mall.
  const neonAccent: StandardMaterial = new StandardMaterial("mm-neon", scene);
  neonAccent.diffuseColor = new Color3(0.75, 0.8, 0.85);
  // Over 1.0 on purpose: survives ACES tone mapping bright enough to bloom.
  neonAccent.emissiveColor = new Color3(1.15, 1.2, 1.3);
  neonAccent.specularColor = new Color3(0, 0, 0);

  // Modern palette: polished light terrazzo floors, white walls, charcoal
  // trim, brushed steel, light oak fixtures.
  const mats: SharedMaterials = {
    neonAccent,
    contactShadow: contactShadowMaterial(scene),
    // 2.5m-format tiles: atrium spans 70m → 28 repeats; corridors and shops
    // repeat less so the tile size stays plausible on smaller meshes.
    atriumFloor: polishedTiles(scene, "mm-atrium-floor", "#eef0f2", "#dcdfe2", 28),
    corridorFloor: polishedTiles(scene, "mm-corridor-floor", "#eef0f2", "#dcdfe2", 8),
    shopFloor: polishedTiles(scene, "mm-shop-floor", "#e7eaec", "#d6d9dc", 5),
    // Tints hold the walls below clipping under the sun (same reasoning as
    // the floor albedo: paint-white walls reflect ~85%, not 96%).
    shopWall: brightWall(scene, "mm-shop-wall", 4, "#e0e3e6"),
    corridorWall: brightWall(scene, "mm-corridor-wall", 6, "#d9dcde"),
    ceiling: (() => {
      const m: PBRMaterial = texturedSurface(
        scene, "mm-ceiling", "/textures/ceiling_color.jpg", "/textures/ceiling_normal.jpg", 8, "#f4f6f8", 0.9, 0,
        () => flat(scene, "mm-ceiling", "#eff2f5", 0.9)
      );
      // Faint self-light suggests recessed cove lighting; ceilings face away
      // from every lamp, and pitch-black ceilings read as a cave. Kept far
      // below the old 0.28 wash that flattened the whole hall.
      m.emissiveColor = new Color3(0.05, 0.051, 0.053);
      return m;
    })(),
    glass,
    frame: flat(scene, "mm-frame", "#aeb4bd", 0.35, 0.8),
    woodFixture: texturedSurface(
      scene, "mm-wood", "/textures/wood_color.jpg", "/textures/wood_normal.jpg", 2, "#e6d6bd", 0.55, 0,
      () => flat(scene, "mm-wood", "#d6bf9f", 0.55)
    ),
    metalFixture: texturedSurface(
      scene, "mm-metal", "/textures/metal_color.jpg", "/textures/metal_normal.jpg", 2, "#eef1f5", 0.3, 0.9,
      () => flat(scene, "mm-metal", "#c9cdd4", 0.3, 0.9)
    ),
    planter: flat(scene, "mm-planter", "#eceef0", 0.4),
    foliage: flat(scene, "mm-foliage", "#3f7a44", 0.85),
    escalatorStep: ribbedTread(scene),
    foodCourtFloor: texturedSurface(
      scene, "mm-food-floor", "/textures/wood_color.jpg", "/textures/wood_normal.jpg", 12, "#e8ddcd", 0.35, 0,
      () => flat(scene, "mm-food-floor", "#e3e0da", 0.35)
    ),
  };
  CACHE.set(scene, mats);
  return mats;
}
